import {
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  PerspectivesGroupsReferenceTypes,
  PerspectivesGroupsWorkspace,
  ReferenceGroupingRule,
} from '../types';
import { TextInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import { GroupLabel } from './SharedGroupRuleElements';
import { ArdoqId, GroupType, ReferenceDirection } from '@ardoq/api-types';
import { getWorkspaceOptions } from './getWorkspaceOptions';
import { DisabledOptionProps } from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

const toReferenceTypeOption = (
  referenceTypes: PerspectivesGroupsReferenceTypes
) => ({
  value: referenceTypes.id.toString(),
  label: referenceTypes.name,
});

const getReferenceDirectionOptions = (i18nLocale: I18nLocale) => [
  {
    value: ReferenceDirection.INCOMING,
    label: t(i18nLocale, 'perspectives:groupingRules.incoming', 'Incoming'),
  },
  {
    value: ReferenceDirection.OUTGOING,
    label: t(i18nLocale, 'perspectives:groupingRules.outgoing', 'Outgoing'),
  },
];

type ReferenceTypeGroupRuleProps = {
  groupingRule: ReferenceGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  referenceTypesPerWorkspace: Record<
    ArdoqId,
    PerspectivesGroupsReferenceTypes[]
  >;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  tabIndex?: number;
  disabledOptionProps?: DisabledOptionProps;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  i18nLocale: I18nLocale;
};

const ReferenceTypeGroupRule = ({
  workspaces,
  groupingRule,
  referenceTypesPerWorkspace,
  onGroupingRuleUpdated,
  disabledOptionProps,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale,
}: ReferenceTypeGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = getWorkspaceOptions({
    workspaces,
    disabledOptionProps,
    entitiesName: 'references',
    entitiesPerWorkspace: referenceTypesPerWorkspace,
    firstSelectOption: workspaceSelectOption,
  });

  const referenceTypes =
    referenceTypesPerWorkspace[groupingRule.workspaceId!] ?? [];
  const referenceTypeOptions = [
    ruleTargetSelectOption,
    ...referenceTypes.map(toReferenceTypeOption),
  ].filter(isTruthy);

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <GroupLabel data-testid={`group-by-${GroupType.REFERENCE}-label`}>
        {t(
          i18nLocale,
          'perspectives:groupingRules.referenceLabel',
          'Reference'
        )}
      </GroupLabel>
      <Select
        dataTestId="reference-type-grouping-rule-workspace-select"
        value={groupingRule.workspaceId}
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultReferenceType =
            referenceTypesPerWorkspace[nextWorkspaceId]?.[0];
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultReferenceType?.id?.toString(),
          });
        }}
      />
      <Select
        dataTestId="reference-type-grouping-rule-reference-types-select"
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.referenceTypesAriaLabel',
          'Reference types'
        )}
        options={referenceTypeOptions}
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event => {
          updateGroupRule({
            targetId: event?.value,
          });
        }}
      />
      <Select
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.referenceDirectionAriaLabel',
          'Reference direction'
        )}
        dataTestId="reference-type-grouping-rule-direction-select"
        value={groupingRule.direction}
        options={getReferenceDirectionOptions(i18nLocale)}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            direction: event?.value ?? ReferenceDirection.INCOMING,
          })
        }
      />
      <TextInput
        value={groupingRule.sublabel}
        dataTestId="reference-type-grouping-rule-sublabel-input"
        placeholder={t(
          i18nLocale,
          'perspectives:groupingRules.addLabelPlaceholder',
          'Add a label'
        )}
        tabIndex={tabIndex}
        onValueChange={value =>
          updateGroupRule({
            sublabel: value,
          })
        }
      />
    </>
  );
};

export { ReferenceTypeGroupRule };
